import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSubjectContext } from '../index';
import {
  Button,
  CircularProgress,
  FabButton,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@dls/react-core';
import { ButtonProps, Grid, GridSize, MenuItem } from '@material-ui/core';
import styled from 'styled-components';
import { GraphContainer } from './graph-container';
import { Sidebar } from './sidebar';
import { DataToolbar } from './toolbar';
import { endOfDay, startOfDay } from 'date-fns';
import { Icon } from '@dls/react-icon';
import { NoDataMessage } from '../../../components/no-data-message';
import { ImportData } from '../../../components/dialogs/import-data';
import { ExportData } from '../../../components/dialogs/export-data';
import { getMeasurementTypes, MeasurementType } from '../../../api/studykit';
import { MetricExporter } from '../../../components/subjects/metric-exporter';
import { SyncStateIndicator } from './sync-state-indicator';
import { useURLAndLocalPersistentState } from './persistence';
import { useLocalPersistedState, useURLPersistedState } from '../../../hooks/use-persisted-state';
import { StudyPageContext } from '../../../App';
import { HasPermission, usePermission } from '../../../context/authorization';
import { DeleteDataDialog } from '../../../components/dialogs/delete-data-dialog';

const DataOverviewContents = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  padding: 24px;
`;

const NoDataContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 100px;
`;

const UNSUPPORTED_MEASUREMENTS = ['WeSTPrivateData', 'Profile', 'UserEvent'];

const useMeasurementTypes = () => {
  const [measurementTypes, setMeasurementTypes] = useState<MeasurementType[]>();
  const { study } = useContext(StudyPageContext);
  const { subject } = useSubjectContext();
  const canQueryData = usePermission('SubjectsData.Query');

  useEffect(() => {
    if (!canQueryData) return;
    getMeasurementTypes(study.id, subject.id)
      .then((r) => r.filter((t) => !UNSUPPORTED_MEASUREMENTS.includes(t.name)))
      .then(setMeasurementTypes);
  }, [study.id, subject.id, canQueryData]);

  const [selectedTypeNames, setSelectedTypeNames] = useURLAndLocalPersistentState<string[]>(
    'metrics',
    [],
  );

  const setSelectedTypes = useCallback(
    (selectedTypes: MeasurementType[]) => {
      setSelectedTypeNames(selectedTypes.map((m) => m.name));
    },
    [setSelectedTypeNames],
  );

  return {
    types: measurementTypes || [],
    selectedTypes: measurementTypes
      ? measurementTypes.filter((m) => selectedTypeNames.includes(m.name))
      : [],
    setSelectedTypes,
  };
};

export const SubjectDataOverview = () => {
  const { study } = useContext(StudyPageContext);
  const { subject } = useSubjectContext();

  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [columnSize, setColumnSize] = useState<1 | 2>(1);

  const { types, selectedTypes, setSelectedTypes } = useMeasurementTypes();
  const [startDate, setStartDate] = useURLPersistedState('start', new Date());
  const [endDate, setEndDate] = useURLPersistedState('end', new Date());
  const [zoomRange, setZoomRange] = useURLPersistedState<[Date, Date] | null>('zoomRange', null);

  const [settings, setSettings] = useLocalPersistedState('graphViewSettings', { showGaps: true });
  const canQueryData = usePermission('SubjectsData.Query');

  const handleResetZoom = useCallback(() => {
    setZoomRange(null);
  }, [setZoomRange]);

  const handeSelectedDateChange = useCallback(
    (startDate: Date, endDate: Date) => {
      setStartDate(startDate);
      setEndDate(endDate);
      setZoomRange(null);
    },
    [setZoomRange, setStartDate, setEndDate],
  );

  const toggleSidebar = useCallback(() => setSidebarOpen(!isSidebarOpen), [isSidebarOpen]);

  const handleMeasurementSelection = useCallback(
    (selected: boolean, type: MeasurementType) => {
      setSelectedTypes(
        selected ? [...selectedTypes, type] : selectedTypes.filter((t) => t !== type),
      );
    },
    [selectedTypes, setSelectedTypes],
  );

  const handleZoom = useCallback(
    (range: [Date, Date]) => {
      setZoomRange(range);
    },
    [setZoomRange],
  );

  const viewRange = zoomRange || [startOfDay(startDate), endOfDay(endDate)];

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar
        open={isSidebarOpen}
        onClose={toggleSidebar}
        onSelection={handleMeasurementSelection}
        measurementTypes={types}
        selected={selectedTypes}
        settings={settings}
        onSettingsChange={setSettings}
      />
      <div style={{ flexGrow: '1' }}>
        <DataToolbar
          open={isSidebarOpen}
          onMenuClick={toggleSidebar}
          onDateChange={handeSelectedDateChange}
          date={startDate}
          zoomRange={zoomRange || undefined}
          rightAlignedContent={
            <ToolbarActions
              columnSize={columnSize}
              setColumnSize={setColumnSize}
              viewRange={viewRange}
              selection={selectedTypes}
            />
          }
        />
        {!canQueryData ? (
          <NoDataContainer>
            <NoDataMessage
              title={'You do not have permission to visualize data'}
              subtitle={
                <>
                  Your account does not have permissions to query subject data.
                  <br />
                  If you need to be able to view subject data, please request the required
                  permissions from a supervisor of this study.
                </>
              }
            />
          </NoDataContainer>
        ) : selectedTypes.length ? (
          <DataOverviewContents>
            <Grid container spacing={4}>
              {selectedTypes.map((measurementType) => {
                return (
                  <Grid key={measurementType.name} item md={(12 / columnSize) as GridSize}>
                    <GraphContainer
                      studyId={study.id}
                      measurementType={measurementType}
                      onResetZoom={handleResetZoom}
                      onZoom={handleZoom}
                      zoomStart={viewRange[0]}
                      zoomEnd={viewRange[1]}
                      subjectId={subject.id}
                      showGaps={settings.showGaps}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </DataOverviewContents>
        ) : (
          types.length > 0 && (
            <NoDataContainer>
              <NoDataMessage
                title={'There are no graphs to display'}
                subtitle={
                  <>
                    You currently did not select any metrics.
                    <br />
                    Please select one or multiple metrics from the sidebar on the left.
                  </>
                }
              />
            </NoDataContainer>
          )
        )}
      </div>
    </div>
  );
};

interface ToolbarActionsProps {
  viewRange: [Date, Date];
  selection: MeasurementType[];
  columnSize: 1 | 2;
  setColumnSize: React.SetStateAction<any>;
}

const ToolbarActions: React.FC<ToolbarActionsProps> = ({
  columnSize,
  setColumnSize,
  viewRange,
  selection,
}) => {
  const { subject } = useSubjectContext();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement>();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const canExportData = usePermission('SubjectsData.Read');
  const canImportData = usePermission('SubjectsData.Create');
  const canDeleteData = usePermission('SubjectsData.Delete');

  const onMenuClick: ButtonProps['onClick'] = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const onMenuClose = () => setAnchorEl(undefined);

  return (
    <div style={{ display: 'flex', columnGap: 8 }}>
      <HasPermission value="SubjectsData.Query">
        <SyncStateIndicator />
        <MetricExporter>
          {({ isSubmitting, triggerExport }) => (
            <Button
              title={'Export current selection'}
              size={'medium'}
              onClick={() =>
                triggerExport(
                  viewRange[0],
                  viewRange[1],
                  selection.map((m) => m.sourceFormatId),
                )
              }
            >
              {isSubmitting ? (
                <CircularProgress dls_size={'small'} variant={'indeterminate'} />
              ) : (
                <Icon name={'DocumentDownload32'} size={'m'} />
              )}
            </Button>
          )}
        </MetricExporter>
        <ToggleButtonGroup
          value={columnSize}
          exclusive
          onChange={(_, newSize) => setColumnSize(newSize)}
          aria-label="text alignment"
        >
          <ToggleButton value={1} aria-label="Single column layout">
            <Icon name={'Layout4x1'} />
          </ToggleButton>
          <ToggleButton value={2} aria-label="Double column layout">
            <Icon name={'Layout4x2'} />
          </ToggleButton>
        </ToggleButtonGroup>
      </HasPermission>
      <FabButton
        style={{ marginLeft: 8 }}
        onClick={onMenuClick}
        size={'small'}
        variant={'extended'}
        dls_variant={'quietDefault'}
      >
        <Icon name={'ThreeDotsHorizontal'} size={'m'} />
      </FabButton>
      <Menu
        getContentAnchorEl={null}
        PaperProps={{
          className: 'DLSMenuList',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={onMenuClose}
      >
        {canExportData && (
          <ExportData open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} />
        )}
        <MenuItem
          onClick={() => {
            onMenuClose();
            setExportDialogOpen(true);
          }}
          disabled={!canExportData}
        >
          <Typography>Export</Typography>
        </MenuItem>
        {canImportData && (
          <ImportData open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />
        )}
        <MenuItem
          onClick={() => {
            onMenuClose();
            setImportDialogOpen(true);
          }}
          disabled={!canImportData}
        >
          <Typography>Import</Typography>
        </MenuItem>
        {canDeleteData && (
          <DeleteDataDialog
            subject={subject}
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
          />
        )}
        <MenuItem
          onClick={() => {
            onMenuClose();
            setDeleteDialogOpen(true);
          }}
          disabled={!canDeleteData}
        >
          <Typography>Delete data</Typography>
        </MenuItem>
      </Menu>
    </div>
  );
};
